Macros generate fresh, unique data every time a workflow runs. Variables let
you reference shared values across nodes. Both use the $ prefix.

AVAILABLE MACROS

  $EMAIL()              Unique test email        test-a1b2@example-test.com
  $FIRST_NAME()         Random first name        Jordan
  $LAST_NAME()          Random last name         Chen
  $PHONE()              Random US phone          +1-555-382-7491
  $UUID()               Random UUID v4           a1b2c3d4-e5f6-...
  $NOW()                ISO 8601 timestamp       2026-02-19T14:30:00.000Z
  $TODAY()              Current date             2026-02-19
  $DATE(N)              Date offset by N days    $DATE(7) = next week
  $TIMESTAMP()          Unix timestamp (ms)      1771520400000
  $RANDOM_INT(min,max)  Random integer           $RANDOM_INT(1,100) = 42
  $RANDOM_STRING(len)   Random alphanumeric      $RANDOM_STRING(6) = xK9mQ4
  $SEQUENCE(name)       Auto-incrementing        $SEQUENCE(order) = 1, 2, 3...

SYNTAX

  Macros follow the format $MACRO_NAME() or $MACRO_NAME(args).
  They can appear standalone or inline within a larger string:

    Standalone:  $EMAIL()
    Inline:      user-$UUID()@corp.com
    Escaped:     \$EMAIL() produces the literal text $EMAIL()

  Each run resolves macros independently — every execution gets fresh values.

VARIABLES

  Variables reference values set during a workflow run. When an action has
  input fields or extracts data, those become $var.{name} references.

  Sources:
    Input         A value entered in an action's field      $var.company
    Macro         A macro expression in a field             $var.email
    Extracted     Data extracted from the page              $var.order_id
    Credential    Values from the login credential          $var.username

  Use $var.{name} in subsequent step formFields and descriptions.
  Don't re-hardcode values — reference the variable from the earlier step.

HOW VARIABLES GET BOUND

  Two distinct paths create $var.{name} bindings. Picking the wrong one is a
  common foot-gun.

  1. Typed input (auto-promote)

     Add an entry to inputValues on an action node:

       inputValues: { company: "$RANDOM_STRING(8)" }

     The key "company" auto-promotes to $var.company for every downstream
     node. Do NOT also list it in declaredOutputs — the input itself IS the
     binding.

  2. DOM capture (declaredOutputs + playbook capture)

     For values that only exist after the page renders (URL fragment, badge
     text, generated ID in the breadcrumb), declare them upfront and capture
     them inside the playbook:

       declaredOutputs: ["order_id"]
       playbookSteps: [
         { order: N, action: "capture", target: "<selector or rule>",
           outputKey: "order_id" }
       ]

     The capture step writes into $var.order_id at runtime. Without
     declaredOutputs the downstream resolver won't recognize the binding.

  Rule of thumb: if you can type the value into a field before the run, it's
  an input. If the value only exists after something happens in the browser,
  it's a captured output.

SEED VARIABLES

  When seed workflows are configured, their outputs are available as
  $var.{dataKey} references. If a seed provides data you need (a project ID,
  a user record), use the seed variable — don't create that data again.

COMMON PATTERNS

  Unique user registration:
    Email: $EMAIL()
    Name: $FIRST_NAME() $LAST_NAME()

  Numbered items:
    $SEQUENCE("orders") → 1, 2, 3... (resets each run, independent per name)

  Date-relative testing:
    $DATE(30) for 30 days out, $DATE(-7) for last week, $TODAY() for now

  Unique identifiers:
    $UUID() for globally unique, $RANDOM_STRING(8) for shorter strings
    Combine with text: order-$RANDOM_STRING(6)

BEST PRACTICES

  - Use $EMAIL() instead of hardcoded addresses for uniqueness
  - Use $SEQUENCE("name") when you need predictable ordering within a run
  - Prefer $DATE(days) over fixed dates so tests stay valid over time
  - Always include a value for every formField — don't leave fields empty